/**
 * Session-lifetime persistence for library filter selections.
 *
 * Library toolbars open on a sensible default (the viewer's home site, their own
 * documents) but must remember whatever the user picks instead for as long as
 * they stay signed in — and forget it afterwards. `sessionStorage` is exactly
 * that lifetime, and every key is scoped by profile so a shared workstation
 * never carries one user's scope into the next sign-in.
 */

function scopedKey(key: string, profileId: string | null | undefined): string {
  return `${key}:${profileId || 'anon'}`;
}

export function readSessionFilterState<T>(
  key: string,
  profileId: string | null | undefined,
): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(scopedKey(key, profileId));
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function writeSessionFilterState<T>(
  key: string,
  profileId: string | null | undefined,
  value: T,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(scopedKey(key, profileId), JSON.stringify(value));
  } catch {}
}

export function clearSessionFilterState(
  key: string,
  profileId: string | null | undefined,
): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(scopedKey(key, profileId));
  } catch {}
}
